import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  RefreshCw,
  AlertCircle,
  DollarSign,
  Link2,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button, Input, Modal, Select } from '../../components/ui';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';

interface Partner {
  id: string;
  name: string;
  document: string;
  type: 'INDICACAO' | 'REVENDA' | 'AFILIADO' | 'OUTRO';
  email: string;
  phone?: string;
  isActive: boolean;
  createdAt: string;
  _count?: {
    commissions: number;
    referrals: number;
  };
}

interface PartnerCommission {
  id: string;
  partnerId: string;
  providerId: string;
  operationType?: string;
  commissionType: 'FIXED' | 'PERCENTAGE';
  commissionValue: number;
  isActive: boolean;
  effectiveFrom: string;
}

export function AdminPartners() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCommissionModalOpen, setIsCommissionModalOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    document: '',
    type: 'INDICACAO' as Partner['type'],
    email: '',
    phone: '',
  });
  const [commissionData, setCommissionData] = useState({
    providerId: 'OPENPIX',
    operationType: '',
    commissionType: 'PERCENTAGE' as 'FIXED' | 'PERCENTAGE',
    commissionValue: '',
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-partners'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.costs.partners);
      const partners = response.data;
      // Handle both array and object response
      if (Array.isArray(partners)) {
        return { partners, total: partners.length };
      }
      return partners as { partners: Partner[]; total: number };
    },
  });

  const { data: commissions, isLoading: commissionsLoading } = useQuery({
    queryKey: ['partner-commissions', selectedPartnerId],
    queryFn: async () => {
      if (!selectedPartnerId) return [];
      const response = await api.get(ENDPOINTS.costs.partnerCommissions(selectedPartnerId));
      const data = response.data;
      // Handle both array and object response
      return Array.isArray(data) ? data : (data.commissions || []) as PartnerCommission[];
    },
    enabled: !!selectedPartnerId,
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await api.post(ENDPOINTS.costs.createPartner, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-partners'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<typeof formData> }) => {
      const response = await api.put(`${ENDPOINTS.costs.partners}/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-partners'] });
      setIsModalOpen(false);
      setEditingPartner(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`${ENDPOINTS.costs.partners}/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-partners'] });
    },
  });

  const addCommissionMutation = useMutation({
    mutationFn: async ({ partnerId, data }: { partnerId: string; data: typeof commissionData }) => {
      const response = await api.post(ENDPOINTS.costs.linkCommission(partnerId), {
        ...data,
        commissionValue: parseFloat(data.commissionValue),
        operationType: data.operationType || null,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-commissions', selectedPartnerId] });
      setCommissionData({
        providerId: 'OPENPIX',
        operationType: '',
        commissionType: 'PERCENTAGE',
        commissionValue: '',
      });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      document: '',
      type: 'INDICACAO',
      email: '',
      phone: '',
    });
  };

  const handleEdit = (partner: Partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      document: partner.document,
      type: partner.type,
      email: partner.email,
      phone: partner.phone || '',
    });
    setIsModalOpen(true);
  };

  const handleViewCommissions = (partnerId: string) => {
    setSelectedPartnerId(partnerId);
    setIsCommissionModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingPartner) {
      updateMutation.mutate({ id: editingPartner.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleAddCommission = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedPartnerId) {
      addCommissionMutation.mutate({ partnerId: selectedPartnerId, data: commissionData });
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('Tem certeza que deseja excluir este parceiro?')) {
      deleteMutation.mutate(id);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const partners = data?.partners || [];
  const filteredPartners = partners.filter(partner =>
    partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    partner.document.includes(searchTerm) ||
    partner.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const typeLabels: Record<Partner['type'], string> = {
    INDICACAO: 'Indicação',
    REVENDA: 'Revenda',
    AFILIADO: 'Afiliado',
    OUTRO: 'Outro',
  };

  const getTypeLabel = (type: string): string => {
    return typeLabels[type as Partner['type']] || type;
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="w-16 h-16 text-red-400" />
        <h2 className="text-xl font-semibold text-surface-100">Erro ao carregar parceiros</h2>
        <Button variant="primary" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Gestão de Parceiros</h1>
          <p className="text-surface-400">Gerencie parceiros e suas comissões</p>
        </div>
        <Button variant="primary" onClick={() => { resetForm(); setEditingPartner(null); setIsModalOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Parceiro
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardBody>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nome, documento ou email..."
                leftIcon={<Search className="w-4 h-4" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Partners List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary-400" />
            Lista de Parceiros
            <Badge variant="info">{filteredPartners.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="animate-pulse bg-surface-800 rounded h-20" />
              ))}
            </div>
          ) : filteredPartners.length === 0 ? (
            <div className="text-center py-12 text-surface-400">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum parceiro encontrado</p>
            </div>
          ) : (
            <div className="divide-y divide-surface-800">
              {filteredPartners.map(partner => (
                <div
                  key={partner.id}
                  className="flex items-center justify-between px-6 py-4 hover:bg-surface-800/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
                      <Users className="w-6 h-6 text-primary-400" />
                    </div>
                    <div>
                      <p className="font-medium text-surface-100">{partner.name}</p>
                      <p className="text-sm text-surface-500">
                        {partner.document} • {partner.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant="info">{getTypeLabel(partner.type)}</Badge>
                    <div className="text-right text-sm">
                      <p className="text-surface-400">
                        {partner._count?.commissions || 0} comissões
                      </p>
                      <p className="text-surface-400">
                        {partner._count?.referrals || 0} indicações
                      </p>
                    </div>
                    <Badge variant={partner.isActive ? 'success' : 'error'}>
                      {partner.isActive ? 'Ativo' : 'Inativo'}
                    </Badge>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleViewCommissions(partner.id)} title="Ver Comissões">
                        <DollarSign className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(partner)} title="Editar">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(partner.id)} title="Excluir">
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Partner Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingPartner(null); resetForm(); }}
        title={editingPartner ? 'Editar Parceiro' : 'Novo Parceiro'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nome"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="CPF/CNPJ"
            value={formData.document}
            onChange={(e) => setFormData({ ...formData, document: e.target.value })}
            required
            disabled={!!editingPartner}
          />
          <Select
            label="Tipo"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value as Partner['type'] })}
            options={[
              { value: 'INDICACAO', label: 'Indicação' },
              { value: 'REVENDA', label: 'Revenda' },
              { value: 'AFILIADO', label: 'Afiliado' },
              { value: 'OUTRO', label: 'Outro' },
            ]}
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
          <Input
            label="Telefone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={createMutation.isPending || updateMutation.isPending}>
              {editingPartner ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Commissions Modal */}
      <Modal
        isOpen={isCommissionModalOpen}
        onClose={() => { setIsCommissionModalOpen(false); setSelectedPartnerId(null); }}
        title="Comissões do Parceiro"
        size="lg"
      >
        <div className="space-y-6">
          {/* Add Commission Form */}
          <form onSubmit={handleAddCommission} className="p-4 bg-surface-800/50 rounded-lg space-y-4">
            <h4 className="font-medium text-surface-200 flex items-center gap-2">
              <Link2 className="w-4 h-4" />
              Vincular Nova Comissão
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Provider"
                value={commissionData.providerId}
                onChange={(e) => setCommissionData({ ...commissionData, providerId: e.target.value })}
                options={[
                  { value: 'OPENPIX', label: 'OpenPix' },
                  { value: 'IUGU', label: 'iugu' },
                  { value: 'ASAAS', label: 'Asaas' },
                ]}
              />
              <Select
                label="Tipo de Operação"
                value={commissionData.operationType}
                onChange={(e) => setCommissionData({ ...commissionData, operationType: e.target.value })}
                options={[
                  { value: '', label: 'Todas as operações' },
                  { value: 'PIX_RECEIVE', label: 'Pix Receber' },
                  { value: 'PIX_SEND', label: 'Pix Enviar' },
                  { value: 'TRANSFER', label: 'Transferência' },
                  { value: 'BOLETO', label: 'Boleto' },
                ]}
              />
              <Select
                label="Tipo de Comissão"
                value={commissionData.commissionType}
                onChange={(e) => setCommissionData({ ...commissionData, commissionType: e.target.value as 'FIXED' | 'PERCENTAGE' })}
                options={[
                  { value: 'PERCENTAGE', label: 'Percentual (%)' },
                  { value: 'FIXED', label: 'Valor Fixo (centavos)' },
                ]}
              />
              <Input
                label={commissionData.commissionType === 'PERCENTAGE' ? 'Percentual (ex: 0.5 = 0.5%)' : 'Valor (centavos)'}
                type="number"
                step="0.01"
                value={commissionData.commissionValue}
                onChange={(e) => setCommissionData({ ...commissionData, commissionValue: e.target.value })}
                required
              />
            </div>
            <div className="flex justify-end">
              <Button type="submit" variant="primary" size="sm" isLoading={addCommissionMutation.isPending}>
                <Plus className="w-4 h-4 mr-1" />
                Adicionar
              </Button>
            </div>
          </form>

          {/* Commissions List */}
          <div>
            <h4 className="font-medium text-surface-200 mb-3">Comissões Ativas</h4>
            {commissionsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse bg-surface-800 rounded h-12" />
                ))}
              </div>
            ) : !commissions || commissions.length === 0 ? (
              <div className="text-center py-8 text-surface-400">
                <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Nenhuma comissão configurada</p>
              </div>
            ) : (
              <div className="space-y-2">
                {commissions.map(commission => (
                  <div
                    key={commission.id}
                    className="flex items-center justify-between p-3 bg-surface-800/30 rounded-lg"
                  >
                    <div>
                      <p className="text-surface-200">
                        {commission.providerId} - {commission.operationType || 'Todas operações'}
                      </p>
                      <p className="text-sm text-surface-400">
                        {commission.commissionType === 'PERCENTAGE'
                          ? `${commission.commissionValue}%`
                          : formatCurrency(commission.commissionValue)}
                      </p>
                    </div>
                    <Badge variant={commission.isActive ? 'success' : 'error'}>
                      {commission.isActive ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}
